/**
 * The question editor. A form, where until now there was a textarea full of XML.
 *
 * An author picks multiple choice or checkboxes, types the question and the options, ticks
 * the correct one or ones, sets the marks, and saves. Optionally they set how many attempts
 * are allowed, when the answer may be shown, and an explanation.
 *
 * WHAT IS UNDERNEATH IS UNCHANGED. Everything a question DOES — parsing, checking the
 * answer, scoring, scaling by the marks, counting attempts, revealing the answer at the right
 * moment — belongs to the platform's own `problem` block and is used exactly as it is. This
 * file is one half of a two-way translation and nothing else. There is no shadow copy of the
 * question, no table of ours, and no scoring logic here. The marks an author types are the
 * same `weight` field the grading pipeline already reads, so the moment a question is saved
 * it is already wired into assignment weighting, the pass mark and the certificate. There is
 * nothing to connect.
 *
 * ROUND-TRIP OR REFUSE, and this component only ever sees the "round-trip" half. A question
 * the converter cannot represent completely never reaches here: the server answers kind
 * `"olx"` with a refusal naming the construct, and `DataEditor` shows that sentence above the
 * raw editor. So there is no state in which this form holds a question it only half
 * understands, which is why there is no code here for one.
 *
 * IT REFUSES RATHER THAN REPAIRS. Several things an author can do produce a question that is
 * legal OLX and a bad question — no correct option, two correct options on a multiple choice,
 * a single option, marks of zero. Every one of them is WARNED about and none of them is
 * silently fixed, because quietly ticking a box the author did not tick is this form editing
 * content nobody asked it to edit. The one hard refusal is an option with no text, which
 * cannot be chosen deliberately and which the server declines to write.
 *
 * THE WAY OUT IS ALWAYS OPEN. "Edit the XML instead" is on the screen at all times. The form
 * is a better way to do the common thing, not a replacement for being able to see what is
 * really stored, and an author who needs the source must never have to guess how to reach it.
 */

import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Banner, Button } from '../../shared/ui';
import { saveQuestion } from '../../shared/lib/editor';
import type {
  Question,
  QuestionMetadata,
  QuestionSettings,
  QuestionType,
  ShowanswerChoice,
} from '../../shared/lib/editor';
import { apiMessage } from '../../shared/lib/library';
import { ApiError } from '../../shared/lib/openedx';
import './component-editor.css';
import './question-editor.css';

/**
 * One option, as the form holds it.
 *
 * `id` exists only so React can keep a row's identity across a reorder — without it, moving
 * an option would remount its input and lose the caret. It never leaves this file.
 *
 * `carrier` travels through untouched. It records whether the option's text was written
 * bare inside `<choice>` or wrapped in a `<div>`, and putting it back where it was found is
 * what keeps an untouched question re-encoding to the bytes it came from.
 */
interface OptionDraft {
  id: number;
  text: string;
  correct: boolean;
  carrier: null | 'div';
}

/**
 * The marks a question is worth when `weight` has never been set.
 *
 * Not a guess and not a house default — it is what upstream's own grading path computes.
 * `weighted_score` leaves the raw score alone when `weight is None`, and for a question with
 * one response element `raw_possible` is 1, because capa's `maxpoints` defaults each input
 * field to "1". Both of the types this form can hold have exactly one response element, so
 * for anything this form can open the answer is always 1.
 *
 * Shown as a PLACEHOLDER and never as a value, which is the point of naming it here: if it
 * were pre-filled into the field, saving an untouched form would write `1` to the 22
 * problems in this estate that have never had a weight, and their marks would stop tracking
 * the platform's default the moment upstream changed it.
 */
const EFFECTIVE_MARKS_WHEN_UNSET = '1';

const TYPE_LABELS: Record<QuestionType, string> = {
  multiple_choice: 'Multiple choice — one correct answer',
  checkboxes: 'Checkboxes — any number of correct answers',
};

/** `null` for a `weight` that was never set; `'0'` for one an author really typed. */
const numberToField = (value: number | null) => (value === null ? '' : String(value));

/**
 * A typed number, or a refusal to interpret it.
 *
 * `Number('')` is 0 and `Number(' ')` is 0, which is exactly the coercion that turns "no
 * marks set" into "worth nothing" — so the empty case is handled before any conversion
 * happens, and a value that is not a number at all is `undefined` rather than `NaN`.
 */
function fieldToNumber(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : undefined;
}

export default function QuestionEditor({
  blockKey,
  question,
  settings,
  showanswerChoices,
  initialName,
  publishTarget,
  readOnly = false,
  onSaved,
  onEditSource,
}: {
  blockKey: string;
  question: Question;
  settings: QuestionSettings;
  /** Every legal `showanswer` value, read off the block's own field. Never a list of ours. */
  showanswerChoices: ShowanswerChoice[];
  initialName: string;
  publishTarget: 'unit' | 'library';
  readOnly?: boolean;
  onSaved: () => void;
  /** Hand over to the raw XML editor. Always available; never the only way forward. */
  onEditSource: () => void;
}) {
  const [type, setType] = useState<QuestionType | null>(question.type);
  const [prompt, setPrompt] = useState(question.prompt.text);
  const [options, setOptions] = useState<OptionDraft[]>(() =>
    question.options.map((option, index) => ({ id: index, ...option })),
  );
  const [nextId, setNextId] = useState(question.options.length);
  const [explanation, setExplanation] = useState(question.explanation ?? '');
  const [name, setName] = useState(initialName);

  /**
   * The three settings, held as STRINGS.
   *
   * This is the single most important line in the file. `weight` empty and `weight` zero are
   * different values with opposite consequences — unset is one mark, zero means the question
   * contributes nothing to any grade — and a `number` input whose value coerces `''` to `0`
   * collapses them. 163 of this estate's 452 questions are already sitting on the zero, with
   * nothing anywhere saying so. Holding the field as the text the author actually typed is
   * what keeps "empty" and "0" tellable apart all the way to the request.
   */
  const [marks, setMarks] = useState(() => numberToField(settings.weight));
  const [attempts, setAttempts] = useState(() => numberToField(settings.max_attempts));
  const [showanswer, setShowanswer] = useState(settings.showanswer ?? '');

  /**
   * What is on the server, as far as this form knows.
   *
   * Tracked here rather than compared against the props, for the reason `DataEditor` records:
   * the platform does not always hand back byte-for-byte what it was sent, so comparing
   * against the props leaves the editor claiming unsaved changes after a save that worked.
   * It also means a refetch cannot overwrite what an author is in the middle of typing.
   */
  const [baseline, setBaseline] = useState(() => ({
    type: question.type,
    prompt: question.prompt.text,
    options: question.options.map((option) => ({ ...option })),
    explanation: question.explanation ?? '',
    marks: numberToField(settings.weight),
    attempts: numberToField(settings.max_attempts),
    showanswer: settings.showanswer ?? '',
    name: initialName,
  }));

  const trimmedName = name.trim();
  const renamed = trimmedName !== baseline.name && trimmedName !== '';

  const contentChanged =
    type !== baseline.type ||
    prompt !== baseline.prompt ||
    explanation !== baseline.explanation ||
    options.length !== baseline.options.length ||
    options.some((option, index) => {
      const was = baseline.options[index];
      return !was || was.text !== option.text || was.correct !== option.correct;
    });

  const settingsChanged =
    marks !== baseline.marks ||
    attempts !== baseline.attempts ||
    showanswer !== baseline.showanswer;

  const dirty = contentChanged || settingsChanged || renamed;

  /** The options in a different order, with the same texts. Worth saying out loud — see below. */
  const reordered = useMemo(() => {
    if (options.length !== baseline.options.length) return false;
    const before = baseline.options.map((option) => option.text);
    const after = options.map((option) => option.text);
    if (before.every((text, index) => text === after[index])) return false;
    return [...before].sort().join(' ') === [...after].sort().join(' ');
  }, [options, baseline.options]);

  const correctChanged = useMemo(
    () =>
      options.length === baseline.options.length &&
      options.some((option, index) => baseline.options[index]?.correct !== option.correct),
    [options, baseline.options],
  );

  const correctCount = options.filter((option) => option.correct).length;
  const marksValue = fieldToNumber(marks);
  const attemptsValue = fieldToNumber(attempts);

  /**
   * What stops a save, as opposed to what merely deserves saying.
   *
   * Kept deliberately short. Everything else in this form is a warning, because refusing to
   * save content an author has written is a serious thing to do and "this question is not
   * very good" is not our call to enforce. The two entries here are the ones where saving
   * would store something that cannot work: an option nobody can pick, and a number the
   * platform cannot store.
   */
  const blockers: string[] = [];
  if (contentChanged) {
    if (type === null) {
      blockers.push('Choose whether this is a multiple choice or a checkbox question.');
    }
    if (options.length === 0) {
      blockers.push('Add at least one option.');
    }
    const blank = options
      .map((option, index) => (option.text.trim() === '' ? index + 1 : null))
      .filter((position): position is number => position !== null);
    if (blank.length === 1) {
      blockers.push(
        `Option ${blank[0]} has no text. An option with no text cannot be chosen, so give it ` +
          'wording or remove it.',
      );
    } else if (blank.length > 1) {
      blockers.push(
        `Options ${blank.join(', ')} have no text. An option with no text cannot be chosen, ` +
          'so give each one wording or remove it.',
      );
    }
  }
  if (marksValue === undefined) blockers.push('Marks must be a number, or empty.');
  if (attemptsValue === undefined) {
    blockers.push('Attempts allowed must be a whole number, or empty.');
  } else if (attemptsValue !== null && !Number.isInteger(attemptsValue)) {
    blockers.push('Attempts allowed must be a whole number.');
  }
  if (marksValue !== null && marksValue !== undefined && marksValue < 0) {
    blockers.push('Marks cannot be negative.');
  }

  const save = useMutation({
    mutationFn: () => {
      /**
       * Only what changed is sent, per field.
       *
       * Not a saving — a correctness requirement, twice over. Sending `weight` on every save
       * would write a number to the 22 questions here that have never had one. Omitting it
       * when the author has CLEARED a previously-set value would leave the old value in
       * place, and the author would watch a successful save do nothing.
       */
      const metadata: QuestionMetadata = {};
      if (marks !== baseline.marks) metadata.weight = marksValue ?? null;
      if (attempts !== baseline.attempts) metadata.max_attempts = attemptsValue ?? null;
      if (showanswer !== baseline.showanswer) metadata.showanswer = showanswer;
      if (renamed) metadata.display_name = trimmedName;

      return saveQuestion(blockKey, {
        // Absent when the author did not touch the question itself, so the OLX is not
        // rewritten at all — see `saveQuestion`. This is what makes "opened it, read it,
        // changed the marks, saved" leave the question's own bytes alone.
        question: contentChanged ? buildQuestion() : undefined,
        metadata,
      });
    },
    onSuccess: () => {
      setBaseline({
        type,
        prompt,
        options: options.map((option) => ({
          text: option.text,
          correct: option.correct,
          carrier: option.carrier,
        })),
        explanation,
        marks,
        attempts,
        showanswer,
        name: renamed ? trimmedName : baseline.name,
      });
      onSaved();
    },
  });

  /**
   * The question, in the shape the converter's encoder takes.
   *
   * Two pieces of care here, both about not editing what the author did not edit:
   *
   * `olx` is the encoder's bookkeeping — the indent the source used, the group tag it used,
   * the root element's own attributes — and it travels straight through. The one time it is
   * touched is when the author has CHANGED THE TYPE, because the recorded group tag then
   * belongs to the old type and the encoder rightly refuses a `checkboxgroup` inside a
   * `multiplechoiceresponse`. Clearing it lets the encoder pick the group that matches. Note
   * this restores the original tag if the author switches back, so a toggle-and-back is still
   * a no-op — including for the questions written as `choiceresponse` + `radiogroup`, which
   * are single-answer questions spelled the other legal way.
   *
   * `explanation` is sent VERBATIM when the author has not touched the box. An explanation
   * that decoded to an empty string is not the same thing as no explanation at all — one
   * emits a `<solution>` element and the other does not — and treating blank as absent would
   * silently delete the element from any question whose explanation body happened to be
   * empty.
   */
  function buildQuestion(): Question {
    const sameType = type === baseline.type;
    return {
      type,
      prompt: { text: prompt, carrier: question.prompt.carrier },
      options: options.map((option) => ({
        text: option.text,
        correct: option.correct,
        carrier: option.carrier,
      })),
      explanation:
        explanation === baseline.explanation
          ? question.explanation
          : explanation.trim() === ''
            ? null
            : explanation,
      empty: false,
      olx: sameType
        ? question.olx
        : { ...question.olx, group_tag: null, group_type: null },
    };
  }

  const move = useCallback((from: number, to: number) => {
    setOptions((current) => {
      if (to < 0 || to >= current.length) return current;
      const next = [...current];
      const [lifted] = next.splice(from, 1);
      if (lifted === undefined) return current;
      next.splice(to, 0, lifted);
      return next;
    });
  }, []);

  const editOption = (id: number, patch: Partial<OptionDraft>) =>
    setOptions((current) =>
      current.map((option) => (option.id === id ? { ...option, ...patch } : option)),
    );

  const showSaved = save.isSuccess && !dirty;

  return (
    <div className="ce-body qedit">
      {readOnly && (
        <p className="ce-note">
          Read only — you do not have author access to this content.
        </p>
      )}

      {/* ------------------------------------------------------------------ the question --- */}

      <label className="ce-field">
        <span className="ce-label">Question type</span>
        <select
          className="ce-input"
          value={type ?? ''}
          disabled={readOnly}
          onChange={(event) => setType((event.target.value || null) as QuestionType | null)}
        >
          {/*
            Only offered while nothing is chosen. A blank question has to start somewhere, but
            an author editing a real question should not be able to put it back into a state
            that cannot be saved.
          */}
          {type === null && <option value="">Choose a type…</option>}
          <option value="multiple_choice">{TYPE_LABELS.multiple_choice}</option>
          <option value="checkboxes">{TYPE_LABELS.checkboxes}</option>
        </select>
        {type !== null && type !== baseline.type && baseline.type !== null && (
          <span className="ce-hint">
            Changing the type rewrites how the options are stored. The wording and which ones
            are correct are kept.
          </span>
        )}
      </label>

      <label className="ce-field">
        <span className="ce-label">Question</span>
        <textarea
          className="ce-input ce-input--multiline qedit__prose"
          rows={3}
          value={prompt}
          disabled={readOnly}
          placeholder="What do you want to ask?"
          onChange={(event) => setPrompt(event.target.value)}
        />
      </label>

      {/* ------------------------------------------------------------------- the options --- */}

      <div className="ce-field">
        <span className="ce-label">
          Options{' '}
          <span className="qedit__sublabel">
            {type === 'checkboxes'
              ? 'tick every correct answer'
              : 'tick the one correct answer'}
          </span>
        </span>

        {options.length === 0 ? (
          <p className="ce-hint">No options yet.</p>
        ) : (
          <ol className="qedit__options">
            {options.map((option, index) => (
              <li key={option.id} className="qedit__option">
                {/*
                  A CHECKBOX FOR BOTH TYPES, deliberately, even though a multiple choice
                  question should have exactly one correct answer. A radio group cannot
                  represent a question that already has two — and 'both are ticked' is a real
                  state in real OLX which capa's own docstring says leaves the learner unable
                  to answer correctly. A control that could not show it would have to silently
                  discard one, which is the form editing content it was only asked to display.
                  So it is shown as it is, and the warning below names the problem.
                */}
                <label className="qedit__correct">
                  <input
                    type="checkbox"
                    checked={option.correct}
                    disabled={readOnly}
                    onChange={(event) =>
                      editOption(option.id, { correct: event.target.checked })
                    }
                  />
                  <span className="qedit__correct-text">Correct</span>
                </label>

                <input
                  className="ce-input qedit__option-text"
                  value={option.text}
                  disabled={readOnly}
                  placeholder={`Option ${index + 1}`}
                  aria-label={`Option ${index + 1}`}
                  onChange={(event) => editOption(option.id, { text: event.target.value })}
                />

                {!readOnly && (
                  <span className="qedit__option-actions">
                    <button
                      type="button"
                      className="qedit__move"
                      aria-label={`Move option ${index + 1} up`}
                      disabled={index === 0}
                      onClick={() => move(index, index - 1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="qedit__move"
                      aria-label={`Move option ${index + 1} down`}
                      disabled={index === options.length - 1}
                      onClick={() => move(index, index + 1)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="qedit__remove"
                      aria-label={`Remove option ${index + 1}`}
                      onClick={() =>
                        setOptions((current) =>
                          current.filter((candidate) => candidate.id !== option.id),
                        )
                      }
                    >
                      Remove
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ol>
        )}

        {!readOnly && (
          <Button
            onClick={() => {
              setOptions((current) => [
                ...current,
                { id: nextId, text: '', correct: false, carrier: null },
              ]);
              setNextId((value) => value + 1);
            }}
          >
            Add an option
          </Button>
        )}
      </div>

      {/* ------------------------------------------------------------------- the warnings --- */}
      {/*
        Everything here is a WARNING and none of it blocks a save. Each names a consequence
        rather than scolding, because every one of these states is legal OLX that occurs in
        real content, and some of them are deliberate — a checkbox question whose answer is
        "none of the above" genuinely has no correct option, and capa's own docstring
        recommends it.
      */}

      {type === 'multiple_choice' && correctCount === 0 && options.length > 0 && (
        <Banner tone="warning">
          <strong>No correct answer is ticked.</strong> Nobody can score this question until
          one is.
        </Banner>
      )}

      {type === 'multiple_choice' && correctCount > 1 && (
        <Banner tone="warning">
          <strong>{correctCount} answers are ticked as correct.</strong> A multiple choice
          question accepts one answer, so a learner has no way to get this right. Tick just
          one, or change the type to checkboxes.
        </Banner>
      )}

      {type === 'checkboxes' && correctCount === 0 && options.length > 0 && (
        <Banner tone="info">
          <strong>Nothing is ticked as correct.</strong> That is allowed — a learner scores by
          leaving every box empty — but it is usually a mistake unless one of the options is
          "none of the above".
        </Banner>
      )}

      {options.length === 1 && (
        <Banner tone="info">
          <strong>This question has one option.</strong> There is nothing to choose between,
          so it will not tell you anything about a learner.
        </Banner>
      )}

      {reordered && (
        <Banner tone="warning">
          <strong>You have changed the order of the options.</strong> Answers already recorded
          against this question are stored by position, not by wording, so if learners have
          answered it their answers will now read as different options. We cannot move them —
          that is how the platform stores them.
        </Banner>
      )}

      {correctChanged && (
        <Banner tone="info">
          <strong>You have changed which options are correct.</strong> Answers learners have
          already submitted are not re-marked. Rescoring is done from the instructor tools.
        </Banner>
      )}

      {/* --------------------------------------------------------------------- the marks --- */}

      <fieldset className="qedit__settings">
        <legend className="ce-label">Marking and answers</legend>

        <label className="ce-field">
          <span className="ce-label">Marks</span>
          <input
            className="ce-input qedit__narrow"
            /*
              A TEXT INPUT, not a number one, and that is not an oversight. `type="number"`
              hands back `''` for anything it considers invalid and invites the reader to coerce
              it, and coercing `''` is how "no marks set" becomes "worth nothing" — the exact
              defect this field exists to avoid. `inputMode` still gets a numeric keypad on a
              phone without giving up the distinction.
            */
            type="text"
            inputMode="decimal"
            value={marks}
            disabled={readOnly}
            /*
              A PLACEHOLDER, NEVER A VALUE. An empty field with `1` behind it says "this is
              worth one mark and nobody has said otherwise". Pre-filling it with `1` would make
              a save of an untouched form write a number to a question that never had one.
            */
            placeholder={EFFECTIVE_MARKS_WHEN_UNSET}
            onChange={(event) => setMarks(event.target.value)}
          />
          <span className="ce-hint">
            {marks.trim() === ''
              ? `Not set, so this question is worth ${EFFECTIVE_MARKS_WHEN_UNSET} mark — the platform's own default.`
              : 'Decimals are allowed, for example 2.5.'}
          </span>
        </label>

        {/*
          THE 163-QUESTION WARNING. `weight: 0` makes a question contribute nothing to any
          grade, and nothing anywhere in the platform says so. Saving is still allowed, because
          zero is a legal value and a survey question is a real thing to want — but it must
          never be reachable silently.
        */}
        {marksValue === 0 && (
          <Banner tone="warning">
            <strong>Zero marks means this question does not count.</strong> It will contribute
            nothing to the grade, and learners will not be told that. If you meant "worth the
            normal one mark", clear the field instead of typing 0.
          </Banner>
        )}

        <label className="ce-field">
          <span className="ce-label">Attempts allowed</span>
          <input
            className="ce-input qedit__narrow"
            type="text"
            inputMode="numeric"
            value={attempts}
            disabled={readOnly}
            placeholder="Unlimited"
            onChange={(event) => setAttempts(event.target.value)}
          />
          <span className="ce-hint">
            {attempts.trim() === ''
              ? 'Leave empty for unlimited attempts.'
              : 'How many times a learner may submit an answer.'}
          </span>
        </label>

        {/*
          `0` IS NOT "UNLIMITED", and an author could reasonably assume it was. It is capa's
          survey mode: the question cannot be submitted for a score at all. Empty is how
          unlimited is expressed, which is why the placeholder says so and why zero is never
          offered as a way to mean it.
        */}
        {attemptsValue === 0 && (
          <Banner tone="warning">
            <strong>Zero attempts is not the same as unlimited.</strong> It makes this a survey
            question that cannot be submitted for a score. For unlimited attempts, clear the
            field.
          </Banner>
        )}

        <label className="ce-field">
          <span className="ce-label">When the answer may be shown</span>
          <select
            className="ce-input"
            value={showanswer}
            disabled={readOnly}
            onChange={(event) => setShowanswer(event.target.value)}
          >
            {/*
              EVERY value the platform's own field accepts, in its own order, under its own
              labels — read off the block by the server, never listed here. A value that exists
              on the field and not in this control is a setting an author could never restore
              once something else had set it.
            */}
            {showanswerChoices.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.display_name}
              </option>
            ))}
          </select>
        </label>

        <label className="ce-field">
          <span className="ce-label">Explanation</span>
          <textarea
            className="ce-input ce-input--multiline qedit__prose"
            rows={3}
            value={explanation}
            disabled={readOnly}
            placeholder="Shown to a learner when the answer is revealed. Optional."
            onChange={(event) => setExplanation(event.target.value)}
          />
        </label>
      </fieldset>

      {/* ---------------------------------------------------------------------- the name --- */}

      {!readOnly && (
        <label className="ce-field">
          <span className="ce-label">Name</span>
          <input
            className="ce-input"
            value={name}
            placeholder={baseline.name}
            onChange={(event) => setName(event.target.value)}
          />
          <span className="ce-hint">
            What this component is called in the unit. Learners do not see it.
          </span>
        </label>
      )}

      {/* ------------------------------------------------------------------- the outcome --- */}

      {blockers.length > 0 && dirty && (
        <Banner tone="danger">
          <strong>Not ready to save.</strong>
          <ul className="qedit__blockers">
            {blockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        </Banner>
      )}

      {save.isError && (
        <Banner tone="danger">
          {/*
            The server's own sentence, when it sent one. A refused question always carries a
            message naming the thing to fix, and replacing it with a generic apology would
            throw away the only useful part of the response.
          */}
          <strong>Not saved.</strong>{' '}
          {(save.error instanceof ApiError ? apiMessage(save.error.body) : null) ??
            'The platform would not accept this question, so nothing has changed. Reopen the component to get the current version.'}
        </Banner>
      )}

      {showSaved && (
        <Banner tone="success">
          <strong>Saved as a draft.</strong>{' '}
          {publishTarget === 'library'
            ? 'Publish the library to put it in front of learners.'
            : 'Publish the unit to show it to learners.'}
        </Banner>
      )}

      {!readOnly && (
        <div className="ce-actions">
          <Button
            variant="primary"
            busy={save.isPending}
            disabled={!dirty || blockers.length > 0}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
          {dirty && (
            <Button
              variant="ghost"
              onClick={() => {
                setType(baseline.type);
                setPrompt(baseline.prompt);
                setOptions(
                  baseline.options.map((option, index) => ({ id: index, ...option })),
                );
                setNextId(baseline.options.length);
                setExplanation(baseline.explanation);
                setMarks(baseline.marks);
                setAttempts(baseline.attempts);
                setShowanswer(baseline.showanswer);
                setName(baseline.name);
              }}
            >
              Revert
            </Button>
          )}
          {dirty && <span className="qedit__dirty">Unsaved changes</span>}
        </div>
      )}

      {/*
        ALWAYS ON THE SCREEN. The form is a better way to do the common thing, not a
        replacement for being able to see what is really stored. An author who needs the XML —
        to copy a question, to check what the form did, to use something the form cannot
        express — must never have to guess how to reach it.
      */}
      <p className="qedit__escape">
        <button type="button" className="qedit__link" onClick={onEditSource}>
          Edit the XML instead
        </button>
        {dirty && ' — unsaved changes here will be lost.'}
      </p>
    </div>
  );
}
